import {jsx} from 'panel';
import type {VNode} from 'snabbdom/vnode';

import {SvgIconEl} from 'common/components/svg-icon';
import {SpinnerEl} from 'common/components/spinner';
import {TooltipEl} from 'common/components/tooltip';

import {
  ItemAndEventHandler,
  ItemHandler,
  SectionItemContextPanelTransform,
  WrappedSection,
  WrappedSectionItem,
} from '../types';

const TooltipWrapper = ({item}, sectionItemElChild) => {
  if (!item.tooltip?.text) {
    return sectionItemElChild;
  }

  return (
    <>
      <TooltipEl
        attrs={{
          placement: item.tooltip?.placement || `right`,
          'always-show': item.tooltip?.[`always-show`] || false,
        }}
      >
        {item.tooltip?.text}
      </TooltipEl>
      <section sel='.list-option-wrapper'>{sectionItemElChild}</section>
    </>
  );
};

export const SectionRowEl = (
  {
    item,
    isFocused,
    isMultiple,
    contextPanelTransform,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: {
    item: WrappedSectionItem;
    isFocused: boolean;
    isMultiple: boolean;
    contextPanelTransform: SectionItemContextPanelTransform;
    onClick: ItemAndEventHandler;
    onMouseEnter: ItemHandler;
    onMouseLeave: ItemHandler;
  },
  sectionItemElChild?: VNode,
) => {
  const hasSubSections = item.subSections && item.subSections.length;
  const isClickEnabled = !(item.isDisabled || item.isReadOnly);
  const selectionType = isMultiple ? `multiple` : `select`;

  return (
    <li
      sel='.list-option'
      class={{
        [`for-${selectionType}-type`]: true,
        disabled: item.isDisabled,
        expander: item.isExpander,
        focused: isFocused,
        'read-only': item.isReadOnly,
        selectable: !(item.isDisabled || item.isReadOnly),
        selected: item.isSelected,
        'has-tooltip': item.tooltip && item.tooltip.text,
        'has-context-panel': contextPanelTransform,
        'has-sub-sections': hasSubSections,
        [`label-theme-${item.labelTheme}`]: item.labelTheme,
      }}
      attrs={{
        elref: item.elref,
      }}
      on={{
        click: isClickEnabled && ((ev) => onClick(item, ev)),
        mouseenter: () => onMouseEnter(item),
        mouseleave: () => onMouseLeave(item),
      }}
    >
      <TooltipWrapper item={item}>{sectionItemElChild}</TooltipWrapper>
    </li>
  );
};

export const SectionEl = (
  {
    section,
  }: {
    section: WrappedSection;
  },
  sectionItemElChildren?: VNode[],
) => (
  <>
    {section.label && (
      <div sel='.section-header'>
        <div sel='.section-label'>
          <span sel='.label'>{section.label}</span>
          {section.icon && (
            <div sel='.section-icon.item-icon' attrs={{icon: section.icon}}>
              <SvgIconEl attrs={{icon: section.icon, empty: !section.icon}} />
            </div>
          )}
        </div>
      </div>
    )}
    {section.isLoading ? (
      <div sel='.loading-items'>
        <SpinnerEl />
      </div>
    ) : (
      <ul sel='.screen-options-list'>{sectionItemElChildren}</ul>
    )}
  </>
);
